import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { COMPANY } from "@/data/siteData";
import { useLanguage, useLocalizedContent } from "@/i18n/LanguageContext";

export default function Footer() {
  const { t } = useLanguage();
  const { categories } = useLocalizedContent();
  return (
    <footer data-testid="site-footer" className="bg-navy-deep text-slate-300">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-8 md:grid-cols-2 lg:grid-cols-4 lg:px-16">
        <div>
          <div>
            <img src="/logo-light.png" alt="Wisdom Insurance Broking" className="h-14 w-auto" />
            <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{COMPANY.tagline}</p>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            {t("footer.blurb")}
          </p>
        </div>

        <div>
          <h4 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold">{t("footer.company")}</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><Link data-testid="footer-link-about" to="/about-us" className="transition-colors hover:text-gold">{t("nav.about")}</Link></li>
            <li><Link data-testid="footer-link-claims" to="/claims-support" className="transition-colors hover:text-gold">{t("nav.claims")}</Link></li>
            <li><Link data-testid="footer-link-personal" to="/personal-insurance" className="transition-colors hover:text-gold">{t("nav.personal")}</Link></li>
            <li><Link data-testid="footer-link-insights" to="/insights" className="transition-colors hover:text-gold">{t("nav.insights")}</Link></li>
            <li><Link data-testid="footer-link-contact" to="/contact" className="transition-colors hover:text-gold">{t("nav.contact")}</Link></li>
            <li><Link data-testid="footer-link-privacy" to="/privacy-policy" className="transition-colors hover:text-gold">Privacy Policy</Link></li>
            <li><Link data-testid="footer-link-disclaimer" to="/disclaimer" className="transition-colors hover:text-gold">Disclaimer</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold">{t("footer.lines")}</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            {categories.map((c) => (
              <li key={c.id}>
                <Link
                  data-testid={`footer-link-${c.id}`}
                  to={`/commercial-insurance#${c.id}`}
                  className="transition-colors hover:text-gold"
                >
                  {c.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-gold">Reach Us</h4>
          <ul className="mt-4 space-y-3 text-sm text-slate-400">
            <li className="flex gap-2.5">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <span data-testid="footer-address">{COMPANY.addressLines.join(", ")}</span>
            </li>
            <li className="flex gap-2.5">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <span data-testid="footer-phone">{COMPANY.phone}</span>
            </li>
            <li className="flex gap-2.5">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <span data-testid="footer-email">{COMPANY.email}</span>
            </li>
          </ul>
        </div>
      </div>

      <div data-testid="regulatory-footer" className="border-t border-slate-800">
        <div className="mx-auto max-w-7xl space-y-2 px-4 py-8 font-mono text-[11px] leading-relaxed text-slate-500 sm:px-8 lg:px-16">
          <p data-testid="reg-irdai">
            IRDAI Broker Licence No.: {COMPANY.irdai.licence} | Category: {COMPANY.irdai.category} | Valid till: {COMPANY.irdai.validTill}
          </p>
          <p data-testid="reg-cin">CIN: {COMPANY.cin}</p>
          <p data-testid="reg-office">Registered Office: {COMPANY.registeredOffice}</p>
          <p data-testid="reg-principal-officer">Principal Officer: {COMPANY.principalOfficer}</p>
          <p data-testid="reg-grievance">
            Grievance Redressal: {COMPANY.grievance.name} | {COMPANY.grievance.email} | {COMPANY.grievance.phone}
          </p>
          <p className="pt-2 text-slate-400" data-testid="reg-solicitation">{t("footer.solicitation")}</p>
          <p data-testid="reg-disclaimer">
            {t("footer.disclaimer")}
          </p>
          <p className="pt-2">© {new Date().getFullYear()} {COMPANY.name}. {t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
}
